<template>
    <Popover @open="handleOpen" @close="handleClose">
        <template #trigger>
            <span class="concession-popover-default-trigger">
                <slot name="trigger">{{ displayConcession }}</slot>
            </span>
        </template>
        <template #content="{ close }">
            <div class="concession-popover-content" v-if="setCloseFunction(close)">
                <slot
                    :close="close"
                    :concession="displayConcession"
                    :carId="carId"
                    :carUuid="carUuid"
                >
                    <div class="concession-info">
                        <div class="concession-name-row">
                            <div class="concession-name">{{ displayConcession }}</div>
                        </div>
                        <div v-if="secondaryInfo" class="concession-id">
                            {{ secondaryInfo }}
                        </div>
                    </div>
                </slot>

                <div class="concession-popover-actions">
                    <Button
                        v-if="showTripsButton"
                        v-tooltip="getTripsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageTrips && isConcessionFiltered"
                        :class="{
                            'current-page': isCurrentPageTrips && isConcessionFiltered,
                        }"
                        @onClick="handleNavigate('trips', close)"
                    >
                        <i class="ri-steering-2-line"></i>
                        <span class="button-hotkey">E</span>
                    </Button>

                    <Button
                        v-if="showShiftsButton"
                        v-tooltip="getShiftsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageShifts && isConcessionFiltered"
                        :class="{
                            'current-page': isCurrentPageShifts && isConcessionFiltered,
                        }"
                        @onClick="handleNavigate('shifts', close)"
                    >
                        <i class="ri-repeat-line"></i>
                        <span class="button-hotkey">S</span>
                    </Button>

                    <Button
                        v-if="showCarsButton"
                        v-tooltip="getCarsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageCars && isConcessionFiltered"
                        :class="{
                            'current-page': isCurrentPageCars && isConcessionFiltered,
                        }"
                        @onClick="handleNavigate('cars', close)"
                    >
                        <i class="ri-car-line"></i>
                        <span class="button-hotkey">F</span>
                    </Button>
                </div>

                <div v-if="$slots.actions" class="concession-popover-custom-actions">
                    <slot
                        name="actions"
                        :close="close"
                        :concession="displayConcession"
                        :carId="carId"
                        :carUuid="carUuid"
                    />
                </div>
            </div>
        </template>
    </Popover>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from '@/components/widgets/Button.vue';
import Popover from '@/components/widgets/Popover.vue';

export default {
    name: 'ConcessionPopover',
    components: { Button, Popover },
    props: {
        concession: {
            type: [String, Number],
            required: true,
            default: '',
        },
        carId: {
            type: [String, Number],
            required: false,
            default: undefined,
            validator: (v) => v === undefined || typeof v === 'string' || typeof v === 'number',
        },
        carUuid: { type: [String, Number], default: null },
        trigger: {
            type: String,
            default: 'click',
            validator: (v) => ['click'].includes(v),
        },
    },
    data() {
        return {
            isOpen: false,
            closeFunction: null,
        };
    },
    computed: {
        ...mapGetters(['isPlanBasic']),

        displayConcession() {
            return String(this.concession);
        },

        secondaryInfo() {
            if (this.carId) {
                return `Fahrzeug Kennzeichen: ${this.carId}`;
            }
            return '';
        },

        currentRoute() {
            return this.$route.path.split('/')[1];
        },

        showTripsButton() {
            return this.isPlanBasic;
        },

        showShiftsButton() {
            return this.isPlanBasic;
        },

        showCarsButton() {
            return this.isPlanBasic;
        },

        isCurrentPageTrips() {
            return this.currentRoute === 'trips';
        },

        isCurrentPageShifts() {
            return this.currentRoute === 'shifts';
        },

        isCurrentPageCars() {
            return this.currentRoute === 'cars';
        },

        canFilterCurrentPage() {
            const filterablePages = ['trips', 'shifts', 'cars'];
            return filterablePages.includes(this.currentRoute);
        },

        isConcessionFiltered() {
            const currentConcession = this.$route.query.concession;
            return currentConcession === String(this.concession);
        },

        availableHotkeys() {
            const hotkeys = [];

            if (this.showTripsButton) {
                hotkeys.push({ key: 'E', label: 'Einzelfahrten' });
            }

            if (this.showShiftsButton) {
                hotkeys.push({ key: 'S', label: 'Schichten' });
            }

            if (this.showCarsButton) {
                hotkeys.push({ key: 'F', label: 'Fahrzeuge' });
            }

            return hotkeys;
        },
    },
    methods: {
        handleOpen() {
            this.isOpen = true;
            document.addEventListener('keydown', this.handleKeydown);

            this.$emit('open', {
                concession: this.displayConcession,
                carId: this.carId,
                carUuid: this.carUuid,
            });
        },
        handleClose() {
            this.isOpen = false;
            this.closeFunction = null;
            document.removeEventListener('keydown', this.handleKeydown);

            this.$emit('close', {
                concession: this.displayConcession,
                carId: this.carId,
                carUuid: this.carUuid,
            });
        },
        setCloseFunction(close) {
            this.closeFunction = close;
            return true;
        },
        handleKeydown(e) {
            if (!this.isOpen || !this.closeFunction) return;

            switch (e.key.toLowerCase()) {
                case 'e':
                    if (this.showTripsButton) {
                        e.preventDefault();
                        this.handleNavigate('trips', this.closeFunction);
                    }
                    break;
                case 's':
                    if (this.showShiftsButton) {
                        e.preventDefault();
                        this.handleNavigate('shifts', this.closeFunction);
                    }
                    break;
                case 'f':
                    if (this.showCarsButton) {
                        e.preventDefault();
                        this.handleNavigate('cars', this.closeFunction);
                    }
                    break;
            }
        },
        handleNavigate(page, close) {
            if (this.isCurrentPage(page) && !this.isConcessionFiltered) {
                this.handleFilterCurrentPage(close);
                return;
            }

            const query = {
                concession: this.concession,
            };

            const currentQuery = this.$route.query;
            if (currentQuery.from) query.from = currentQuery.from;
            if (currentQuery.to) query.to = currentQuery.to;
            if (currentQuery.car) query.car = currentQuery.car;
            if (currentQuery.driver) query.driver = currentQuery.driver;

            this.$router.push({ path: `/${page}`, query });
            close();
        },
        handleFilterCurrentPage(close) {
            this.$emit('filter', {
                concession: this.concession,
                carId: this.carId,
            });
            close();
        },

        getTripsTooltip() {
            if (this.isCurrentPageTrips) {
                return this.isConcessionFiltered
                    ? 'Aktuelle Seite'
                    : 'Nach dieser Konzession filtern (E)';
            }
            return 'In Einzelfahrten Anzeigen (E)';
        },
        getShiftsTooltip() {
            if (this.isCurrentPageShifts) {
                return this.isConcessionFiltered
                    ? 'Aktuelle Seite'
                    : 'Nach dieser Konzession filtern (S)';
            }
            return 'In Schichten Anzeigen (S)';
        },
        getCarsTooltip() {
            if (this.isCurrentPageCars) {
                return this.isConcessionFiltered
                    ? 'Aktuelle Seite'
                    : 'Nach dieser Konzession filtern (F)';
            }
            return 'In Fahrzeuge Anzeigen (F)';
        },
        isCurrentPage(page) {
            return this.currentRoute === page;
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
};
</script>

<style lang="scss" scoped>
.concession-popover-default-trigger {
    font-weight: 500;
    color: var(--color-blue-dark);
    padding: 2px 4px;
    border-radius: 3px;
    border: 0.5px solid rgba(167, 167, 167, 0.3);
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: rgba(167, 167, 167, 0.2);

    &:hover {
        background-color: rgba(167, 167, 167, 0.4);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
}
.concession-popover-content {
    display: flex;
    flex-direction: column;
}
.concession-info {
    margin-bottom: 4px;
    .concession-name-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 1px;
    }
    .concession-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--color-text-black);
        flex: 1;
    }
    .concession-id {
        font-size: 0.75rem;
        color: var(--color-gray);
    }
}
.concession-popover-actions {
    margin-top: 6px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;

    .Button {
        i {
            width: 1em;
            text-align: center;
        }
    }
}
.concession-popover-custom-actions {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 0.5px solid rgba(0, 0, 0, 0.08);
}
.button-hotkey {
    font-weight: 600;
    color: var(--color-text-black);
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
    margin-left: 4px;
    font-size: 1rem;
    font-family: 'Courier New', monospace;
}

.current-page {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
        opacity: 0.5;
    }

    .button-hotkey {
        opacity: 0.5;
    }
}
</style>
